import {isTaxableCategory, resolveVatCategory} from '@vat/engine/resolve-vat-category';
import {round2, signedAmount} from '@vat/engine/rounding';
import type {BookingArtLite, BookingLite, PaymentLite, PeriodLite, VatEngineRange, VatTaxStatus} from '@vat/engine/types';
import type {Property} from '@properties/models/property.interface';
import {DateTime} from 'luxon';
import {VAT_COMMON_COPY} from '../vat-copy';

/**
 * Reine Helfer des Datenladens (Spec 5): Ladefenster, Chunking für `in`-Queries, § 19-Umsatzregel,
 * begrenzte Parallelität, Index-Fehler. Kein Angular, kein Firebase.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
export const IN_CHUNK_SIZE = 30;
export const BATCH_SIZE = 300;
export const PARALLEL_PROPERTIES = 4;

export const chunk = <T>(items: readonly T[], size: number): T[][] => {
    const result: T[][] = [];
    for (let start = 0; start < items.length; start += size) {
        result.push(items.slice(start, start + size));
    }
    return result;
};

export interface LoadWindow {
    start: Date;        // range.start − 1 Monat, 00:00 lokal
    end: Date;          // range.end + 1 Monat, 23:59:59.999 lokal
    expenseEnd: Date;   // range.end + 3 Monate (Rechnungen kommen nach dem Leistungsmonat)
}

export const buildLoadWindow = (range: Pick<VatEngineRange, 'start' | 'end'>): LoadWindow => ({
    start: DateTime.fromJSDate(range.start).minus({months: 1}).startOf('day').toJSDate(),
    end: DateTime.fromJSDate(range.end).plus({months: 1}).endOf('day').toJSDate(),
    expenseEnd: DateTime.fromJSDate(range.end).plus({months: 3}).endOf('day').toJSDate(),
});

/** Spec 5: `art in (WARM_MIETE, KALT_MIETE, BK, SP, CUSTOM)` – BK ist als 'BETRIEBSKOSTEN' gespeichert. */
export const PAYMENT_ARTS_IN: readonly BookingArtLite[] = ['WARM_MIETE', 'KALT_MIETE', 'BETRIEBSKOSTEN', 'SP', 'CUSTOM'];

/** Sollstellungs-Arten, die zum Gesamtumsatz zählen (R4-Arten plus veraltetes GUTHABEN). */
export const TURNOVER_ARTS: ReadonlySet<BookingArtLite> = new Set<BookingArtLite>(['WARM_MIETE', 'KALT_MIETE', 'BETRIEBSKOSTEN', 'GUTHABEN', 'SP', 'CUSTOM']);

/**
 * § 19 Abs. 1/3 UStG: Gesamtumsatz ohne steuerfreie Vermietung (§ 4 Nr. 12). Es zählen nur
 * Sollstellungen, deren Mietverhältnis steuerpflichtig eingeordnet ist (gesetzt oder abgeleitet).
 */
export const taxableTurnoverOfBookings = (bookings: BookingLite[], periodsByID: ReadonlyMap<string, PeriodLite>): number => {
    let sum = 0;
    for (const booking of bookings) {
        if (booking.status === 'IGNORED' || booking.art === null || !TURNOVER_ARTS.has(booking.art) || booking.amount === null) {
            continue;
        }
        const period = booking.periodID ? periodsByID.get(booking.periodID) : undefined;
        if (!period || !isTaxableCategory(resolveVatCategory(period).category)) {
            continue;
        }
        sum += signedAmount(booking.amount, booking.type) - (booking.rentReductionAmount ?? 0);
    }
    return round2(sum);
};

export const needsMonitor19 = (statusAtStart: VatTaxStatus, periods: PeriodLite[]): boolean =>
    statusAtStart === 'KLEINUNTERNEHMER'
    || (statusAtStart === 'STEUERFREI' && periods.some(period => isTaxableCategory(resolveVatCategory(period).category)));

export interface MonitorRanges {
    vorjahr: { start: Date; end: Date };
    laufend: { start: Date; end: Date } | null;   // null, wenn der Zeitraum am 1. Januar beginnt
}

export const monitorRanges = (range: VatEngineRange): MonitorRanges => {
    const yearStart = DateTime.local(range.year, 1, 1).startOf('day');
    const laufendEnd = DateTime.fromJSDate(range.start).minus({days: 1}).endOf('day');
    return {
        vorjahr: {start: yearStart.minus({years: 1}).toJSDate(), end: yearStart.minus({days: 1}).endOf('day').toJSDate()},
        laufend: laufendEnd < yearStart ? null : {start: yearStart.toJSDate(), end: laufendEnd.toJSDate()},
    };
};

export const mergeUniqueByID = <T extends { id: string }>(...lists: T[][]): T[] => {
    const seen = new Set<string>();
    const result: T[] = [];
    for (const list of lists) {
        for (const item of list) {
            if (!seen.has(item.id)) {
                seen.add(item.id);
                result.push(item);
            }
        }
    }
    return result;
};

export const bookingIDsWithHaben = (bookings: BookingLite[]): string[] =>
    bookings.filter(booking => booking.haben !== 0).map(booking => booking.id);

export const missingBookingIDs = (payments: PaymentLite[], bookings: BookingLite[]): string[] => {
    const known = new Set(bookings.map(booking => booking.id));
    return [...new Set(payments.map(payment => payment.bookingID).filter((id): id is string => !!id && !known.has(id)))];
};

/** Begrenzte Parallelität (Spec 5: „vier Immobilien parallel“); Ergebnisreihenfolge = Eingabereihenfolge. */
export const runLimited = async <T, R>(items: readonly T[], limit: number, worker: (item: T, index: number) => Promise<R>): Promise<R[]> => {
    const results: R[] = new Array(items.length);
    let next = 0;
    const lanes = Array.from({length: Math.max(1, Math.min(limit, items.length))}, async () => {
        while (next < items.length) {
            const index = next++;
            results[index] = await worker(items[index], index);
        }
    });
    await Promise.all(lanes);
    return results;
};

export const isIndexMissingError = (err: unknown): boolean =>
    /requires an index/i.test(String((err as { message?: string })?.message ?? err ?? ''));

export const errorMessageFor = (err: unknown, fallback: string): string =>
    isIndexMissingError(err) ? VAT_COMMON_COPY.indexMissing : fallback;

/** Anzeige-Label einer Immobilie ohne Angular-Abhängigkeit (helpers/address.ts importiert @angular/forms). */
export const propertyLabel = (property: Pick<Property, 'h_id_text' | 'address'> | null | undefined, fallbackID: string): string => {
    const address = property?.address;
    const street = [address?.streetName, address?.streetNumber].filter(Boolean).join(' ');
    const city = [address?.postalCode, address?.locality?.long].filter(Boolean).join(' ');
    const addressLabel = [street, city].filter(part => part.length > 0).join(', ');
    const parts = [property?.h_id_text, addressLabel].filter(part => !!part && part.length > 0);
    return parts.length ? parts.join(' · ') : fallbackID;
};
